import { computed, ref } from 'vue'
import { FormFieldTypeEnum, type FormField } from '@/types/generated'
import {
  validateField as validateFieldBase,
  type FieldValidation
} from '@/services/validation.service'

export interface ValidationChangeEvent {
  documentId: string
  fieldId: string
  error: string | undefined
}

export function usePdfFormValidation(
  getFieldValue: (id?: string) => any,
  options?: {
    documentId?: string
    externalErrors?: Map<string, string>
    onValidationChange?: (event: ValidationChangeEvent) => void
  }
) {
  const useExternalErrors = options?.externalErrors !== undefined
  const internalErrors = ref<Map<string, string>>(new Map())

  const getErrorsMap = (): Map<string, string> => {
    return useExternalErrors ? options!.externalErrors! : internalErrors.value
  }

  const checkIfEmpty = (value: any, fieldType: FormFieldTypeEnum): boolean => {
    if (value === null || value === undefined || value === '') {
      return true
    }

    if (fieldType === FormFieldTypeEnum.CHECKBOX) {
      return value === false || value === 'false'
    }

    if (fieldType === FormFieldTypeEnum.SIGNATURE) {
      return !value || value === ''
    }

    if (typeof value === 'string') {
      return value.trim() === ''
    }

    return false
  }

  const validateField = (field: FormField, value: any): { valid: boolean; error?: string } => {
    if (field.required) {
      const isEmpty = checkIfEmpty(value, field.type)
      if (isEmpty) {
        return {
          valid: false,
          error: 'Dieses Feld ist erforderlich.'
        }
      }
    }

    if (field.type === 'signature') {
      return { valid: true }
    }

    if (value || field.type === 'checkbox') {
      const fieldValidation: FieldValidation = {
        tag: field.tag,
        tag_relationship: field.tag_relationship as any,
        type: field.type,
        optional: !field.required
      }

      const result = validateFieldBase(fieldValidation, value)

      if (!result.valid && result.errors && result.errors.length > 0) {
        return {
          valid: false,
          error: result.errors[0]
        }
      }
    }

    return { valid: true }
  }

  const validateSingleField = (field: FormField) => {
    if (!field.uuid) return

    const value = getFieldValue(field.uuid)
    const result = validateField(field, value)

    const errorValue = result.valid ? undefined : result.error
    const errorsMap = getErrorsMap()

    if (!result.valid && result.error) {
      errorsMap.set(field.uuid, result.error)
    } else {
      errorsMap.delete(field.uuid)
    }

    if (options?.onValidationChange && options?.documentId) {
      options.onValidationChange({
        documentId: options.documentId,
        fieldId: field.uuid,
        error: errorValue
      })
    }
  }

  const validateAllFields = (fieldsToValidate: FormField[]): boolean => {
    const errorsMap = getErrorsMap()
    errorsMap.clear()

    fieldsToValidate.forEach((field) => {
      if (field.uuid) {
        const value = getFieldValue(field.uuid)
        const result = validateField(field, value)

        if (!result.valid && result.error) {
          errorsMap.set(field.uuid, result.error)

          if (options?.onValidationChange && options?.documentId) {
            options.onValidationChange({
              documentId: options.documentId,
              fieldId: field.uuid,
              error: result.error
            })
          }
        }
      }
    })

    return errorsMap.size === 0
  }

  const getFieldError = (fieldId?: string): string | undefined => {
    if (!fieldId) return undefined
    return getErrorsMap().get(fieldId)
  }

  const hasErrors = computed(() => {
    return getErrorsMap().size > 0
  })

  const clearErrors = () => {
    getErrorsMap().clear()
  }

  return {
    validationErrors: internalErrors,
    validateSingleField,
    validateAllFields,
    getFieldError,
    hasErrors,
    clearErrors
  }
}
